import { FormTab, FormTabProps, useGetList, useRecordContext, useRedirect } from 'react-admin';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { OrganizationRecord, PassportRecord } from 'src/types';
import { useCompanySubscriptionSets } from 'src/resources/portal/organizations/hooks/useCompanySubscriptionSets';
import { usePassportScopes } from 'src/resources/portal/passport/usePassportScopes';
import { OrganizationEditTab } from 'src/utils/routeHelpers';

const PassportsTab = (props: Omit<FormTabProps, 'label'>) => {
  const record = useRecordContext<OrganizationRecord>();
  const redirect = useRedirect();
  const { scopeKeys } = usePassportScopes();
  const { data: passports } = useGetList<PassportRecord>(
    'passport',
    {
      filter: { authority_org_id: record?.guid, 'deleted_at@_is_null': true },
      sort: { field: 'created_at', order: 'DESC' },
      meta: { noLimit: true },
    },
    { enabled: !!record?.guid },
  );
  const { companySubscriptionSets } = useCompanySubscriptionSets(record?.company_data_api_key);
  const subscriptionSetNameMap = new Map(
    companySubscriptionSets.map((subscriptionSet) => [Number(subscriptionSet.id), subscriptionSet.name]),
  );

  return (
    <FormTab label="Passports" {...props}>
      <Stack spacing={2} width="100%">
        {passports?.map((passport: PassportRecord) => {
          const labelMap = new Map(passport.passport_scope_labels.map((l) => [l.scope_key, l.display_label]));

          return (
            <Paper
              key={passport.id}
              sx={{
                p: 3,
                width: '100%',
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="subtitle1" fontWeight={600}>
                  {passport.name}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() =>
                    redirect(
                      `/passport/${passport.id}?fromOrgId=${record?.id}&fromOrgTab=${OrganizationEditTab.PASSPORTS}`,
                    )
                  }
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Box>
              {passport.description && (
                <Typography variant="body2" color="text.secondary" mb={2}>
                  {passport.description}
                </Typography>
              )}
              <Paper variant="elevation" sx={{ p: 2, mt: 2 }}>
                <Typography variant="subtitle1" mb={1}>
                  Scope Mapping
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <Typography variant="subtitle2">Scope Key</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="subtitle2">Custom Label</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="subtitle2">Custom Description</Typography>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {scopeKeys.map((scopeKey) => {
                        const scopeLabel = passport.passport_scope_labels.find((l) => l.scope_key === scopeKey);
                        return (
                          <TableRow key={scopeKey}>
                            <TableCell>
                              <Typography variant="body2">{scopeKey}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{scopeLabel?.display_label || '—'}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{scopeLabel?.display_description || '—'}</Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>

              {passport.passport_subscription_set_scopes.length > 0 && (
                <Paper variant="elevation" sx={{ p: 2, mt: 2 }}>
                  <Typography variant="subtitle1" mb={1}>
                    Subscription Set Mappings
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <Typography variant="subtitle2">Subscription Set</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="subtitle2">Scope</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="subtitle2">Participants</Typography>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {passport.passport_subscription_set_scopes.map((setScope) => {
                          const scopeLabel =
                            setScope.passport_scope_label?.display_label ??
                            labelMap.get(setScope.scope_key) ??
                            setScope.scope_key;
                          const participants = setScope.participants ?? [];

                          return (
                            <TableRow key={setScope.subscription_set_id}>
                              <TableCell>
                                <Typography variant="body2">
                                  {subscriptionSetNameMap.get(setScope.subscription_set_id) ??
                                    `Set #${setScope.subscription_set_id}`}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">{scopeLabel}</Typography>
                              </TableCell>
                              <TableCell>
                                <Box display="flex" flexWrap="wrap" gap={0.5}>
                                  {participants.map((p) => (
                                    <Chip
                                      key={p.participant_org_id}
                                      label={p.participant?.name ?? p.participant_org_id}
                                      size="small"
                                    />
                                  ))}
                                </Box>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              )}
            </Paper>
          );
        })}

        <Box>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() =>
              redirect(`/passport/create?fromOrgId=${record?.id}&fromOrgTab=${OrganizationEditTab.PASSPORTS}`)
            }
          >
            Add Passport
          </Button>
        </Box>
      </Stack>
    </FormTab>
  );
};

export default PassportsTab;
